"use client";

import React, { createContext, useContext, useMemo } from "react";
import ptBR from "./messages/pt-BR.json";
import enUS from "./messages/en-US.json";
import ptPT from "./messages/pt-PT.json";

export type Language = "pt-BR" | "en-US" | "pt-PT";

type Messages = Record<string, string>;

type I18nContextType = {
  lang: Language;
  t: (key: string, fallback?: string) => string;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const registry: Record<Language, Messages> = {
  "pt-BR": ptBR as Messages,
  "en-US": enUS as Messages,
  "pt-PT": ptPT as Messages,
};

export function I18nProvider({
  language,
  children,
}: Readonly<{
  language: Language;
  children: React.ReactNode;
}>) {
  const messages = useMemo(
    () => registry[language] ?? registry["pt-BR"],
    [language]
  );

  const value = useMemo<I18nContextType>(
    () => ({
      lang: language,
      t: (key: string, fallback?: string) => messages[key] ?? fallback ?? key,
    }),
    [language, messages]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
